import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { TypedMemo } from '../../../common/types';
import { catalogArtworkSrc } from '../../../smartLinks/catalog/catalogArtwork';
import { SongUploadDialog } from '../../soundcloud/upload/SongUploadDialog';
import { ServiceExtras } from '../extras/ServiceExtras';
import { ExtraFileThumb } from '../extras/ExtraFileThumb';
import { ServiceExtraAction, ServiceExtraItem } from '../extras/serviceExtraTypes';
import { CancelUploadDialog } from '../CancelUploadDialog';
import { soundCloudExtrasMessages as m } from './soundCloudExtrasMessages';
import {
  SOUNDCLOUD_ACTION_LABELS,
  soundCloudChipText,
  soundCloudChipTone,
  soundCloudSentence,
  soundCloudSentenceIsBad,
} from './soundCloudExtraTexts';
import { useSoundCloudExtras } from './useSoundCloudExtras';

interface SoundCloudExtrasProps {
  accountId: number;
  item: CatalogItem;
  /** The demo artist: the box with no button. */
  readOnly: boolean;
}

/**
 * "SoundCloud extras" in the SoundCloud row of "Where it plays" (design service-extras a1, a2): the
 * song upload moved here from the row actions. One item, "Song", with the states ready, waiting
 * for its time, uploading, on SoundCloud, failed, signed out and no master. The upload dialog
 * (design c3-c6) and the cancel question stay the same.
 */
const SoundCloudExtrasComponent: React.FC<SoundCloudExtrasProps> = ({ accountId, item, readOnly }) => {
  const intl = useIntl();
  const extras = useSoundCloudExtras({ accountId, item, readOnly });
  const { view, publish, run, isBusy } = extras;
  const artwork = catalogArtworkSrc(item, 96) ?? null;

  const items = useMemo((): ServiceExtraItem[] => {
    const actions = view.actions.map(({ action, look }): ServiceExtraAction => {
      const label = intl.formatMessage(SOUNDCLOUD_ACTION_LABELS[action]);
      if (action === 'open' && view.url !== null) {
        return { id: action, label, look: 'link', href: view.url };
      }
      return { id: action, label, look, disabled: isBusy, onClick: () => run(action) };
    });
    return [
      {
        id: 'soundCloudSong',
        title: intl.formatMessage(m.song),
        thumb: (
          <ExtraFileThumb
            shape="square"
            image={artwork}
            empty={view.state === 'noMaster'}
            dim={view.state === 'connect'}
            mark="soundcloud"
          />
        ),
        chips:
          view.chip === null
            ? []
            : [
                {
                  id: view.chip,
                  text: soundCloudChipText(intl, view.chip, publish),
                  tone: soundCloudChipTone(view.chip),
                },
              ],
        status: soundCloudSentence(intl, view, publish),
        statusIsBad: soundCloudSentenceIsBad(view),
        actions,
        waits: view.waits,
      },
    ];
  }, [artwork, intl, isBusy, publish, run, view]);

  return (
    <>
      <ServiceExtras label={intl.formatMessage(m.label)} items={items} testId="soundcloud-extras" />
      {extras.dialog !== null && (
        <SongUploadDialog
          accountId={accountId}
          catalogItemUuid={item.uuid}
          mode={extras.dialog}
          onConnect={extras.connect}
          onClose={extras.closeDialog}
        />
      )}
      <CancelUploadDialog
        accountId={accountId}
        publish={extras.cancelling}
        songTitle={item.title}
        onClose={extras.closeCancel}
      />
    </>
  );
};

export const SoundCloudExtras = TypedMemo(SoundCloudExtrasComponent);
